<style scoped>
    .action-link {
        cursor: pointer;
    }
</style>

<template>
    <div>
        <div class="module-header">
            <h5 class="module-title">{{ $t('words.oauth_clients')}}</h5>
        </div>
        <div class="module-body">
            <div class="module-action-group" style="margin-bottom: 24px">
                <a class="btn" tabindex="-1" v-if="haveSubmitRight" @click="showCreateClientForm">
                    {{ $t('words.create_new_client')}}
                </a>
                <nav aria-label="Page navigation" class="ml-auto">
                    <ul class="pagination">
                        <li class="page-info">
                            <span>
                            {{ $t('paragraphs.page_navigation', { total: meta.total, from: ((meta.current_page - 1) * meta.per_page + 1), to: (meta.current_page >= meta.last_page ? meta.total : meta.per_page * meta.current_page)}) }}
                            </span>
                        </li>
                        <li :class="{'page-item': true, disabled: meta.current_page <= 1 }">
                            <button class="page-link" aria-label="Previous" @click="moveCurrentPage(meta.current_page-1)" v-if="meta.current_page > 1">
                                <i class="icon icon-arrow-left-sm" aria-hidden="true"></i>
                                <span class="sr-only">{{ $t('pagination.previous') }}</span>
                            </button>
                            <button class="page-link" aria-label="Previous" v-else>
                                <i class="icon icon-arrow-left-sm" aria-hidden="true"></i>
                                <span class="sr-only">{{ $t('pagination.previous') }}</span>
                            </button>
                        </li>
                        <li :class="{'page-item': true, disabled: meta.last_page <= meta.current_page }">
                            <button class="page-link" aria-label="Next" @click="moveCurrentPage(meta.current_page+1)" v-if="meta.current_page < meta.last_page">
                                <i class="icon icon-arrow-right-sm" aria-hidden="true"></i>
                                <span class="sr-only">{{ $t('pagination.next') }}</span>
                            </button>
                            <button class="page-link disabled" aria-label="Previous" disabled v-else>
                                <i class="icon icon-arrow-right-sm" aria-hidden="true"></i>
                                <span class="sr-only">{{ $t('pagination.next') }}</span>
                            </button>
                        </li>
                    </ul>
                </nav>
            </div>
            <div class="table__wide">
                <table class="table mb-0">
                    <thead>
                    <tr>
                        <th>{{ $t('words.client_id')}}</th>
                        <th>{{ $t('words.name')}}</th>
                        <th>{{ $t('words.secret')}}</th>
                        <th></th>
                    </tr>
                    </thead>

                    <tbody v-if="clients.length > 0">
                    <tr v-for="client in currentClients" :key="client.id">
                        <!-- ID -->
                        <td>
                            {{ client.id }}
                        </td>

                        <!-- Name -->
                        <td>
                            {{ client.name }}
                        </td>

                        <!-- Secret -->
                        <td>
                            <code>{{ client.secret }}</code>
                        </td>

                        <!--  Edit / Delete Button -->
                        <td class="td__action">
                            <button type="submit" class="btn btn-icon btn-flat" v-if="haveSubmitRight" @click.prevent="edit(client)">
                                <i class="icon icon-edit"></i>
                            </button>
                            <button type="submit" class="btn btn-icon btn-flat" v-if="haveSubmitRight" @click.prevent="destroy(client)">
                                <i class="icon icon-trash"></i>
                            </button>
                        </td>
                    </tr>
                    </tbody>
                    <tbody v-else>
                    <tr>
                        <td colspan="4" class="td__message">
                            <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 24 24" class="svg-icon svg-icon-alert-circle"><g stroke-linecap="square" stroke-linejoin="miter" stroke-width="2" fill="#adb5bd" stroke="#adb5bd"><circle fill="none" stroke="#adb5bd" stroke-miterlimit="10" cx="12" cy="12" r="11"/> <line data-color="color-2" fill="none" stroke-miterlimit="10" x1="12" y1="7" x2="12" y2="13"/> <circle data-color="color-2" data-stroke="none" cx="12" cy="17" r="1" stroke-linejoin="miter" stroke-linecap="square" stroke="none"/></g></svg>
                            {{ $t('paragraphs.oauth_no_clients')}}
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Create Client Modal -->
        <div class="modal fade" id="modal-create-client" tabindex="-1" role="dialog">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h4 class="modal-title">
                            {{ $t('words.create_client')}}
                        </h4>

                        <button type="button" class="close" data-dismiss="modal" aria-hidden="true">&times;</button>
                    </div>

                    <div class="modal-body">
                        <!-- Form Errors -->
                        <div class="alert alert-danger" v-if="createForm.errors.length > 0">
                            <!--<p class="mb-0"><strong>Whoops!</strong> Something went wrong!</p>-->
                            <ul>
                                <li v-for="error in createForm.errors">
                                    {{ error }}
                                </li>
                            </ul>
                        </div>

                        <!-- Create Client Form -->
                        <form role="form">
                            <!-- Name -->
                            <div class="form-group row">
                                <label class="col-md-3 col-form-label">{{ $t('words.name')}}</label>

                                <div class="col-md-9">
                                    <input id="create-client-name" type="text" class="form-control"
                                           @keyup.enter="store" v-model="createForm.name">

                                    <span class="form-text text-muted">
                                        {{ $t('paragraphs.oauth_name_tip')}}
                                    </span>
                                </div>
                            </div>

                            <!-- Redirect URL -->
                            <div class="form-group row">
                                <label class="col-md-3 col-form-label">{{ $t('words.redirect_url')}}</label>

                                <div class="col-md-9">
                                    <input type="text" class="form-control" name="redirect"
                                           @keyup.enter="store" v-model="createForm.redirect">

                                    <span class="form-text text-muted">
                                        {{ $t('paragraphs.oauth_redirect_url_tip')}}
                                    </span>
                                </div>
                            </div>
                        </form>
                    </div>

                    <!-- Modal Actions -->
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-dismiss="modal">{{ $t('words.close')}}</button>

                        <button type="button" class="btn btn-primary" @click="store">
                            {{ $t('words.save')}}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Edit Client Modal -->
        <div class="modal fade" id="modal-edit-client" tabindex="-1" role="dialog">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h4 class="modal-title">
                            {{ $t('words.edit_client')}}
                        </h4>

                        <button type="button" class="close" data-dismiss="modal" aria-hidden="true">&times;</button>
                    </div>

                    <div class="modal-body">
                        <!-- Form Errors -->
                        <div class="alert alert-danger" v-if="editForm.errors.length > 0">
                            <!--<p class="mb-0"><strong>Whoops!</strong> Something went wrong!</p>-->
                            <br>
                            <ul>
                                <li v-for="error in editForm.errors">
                                    {{ error }}
                                </li>
                            </ul>
                        </div>

                        <!-- Edit Client Form -->
                        <form role="form">
                            <!-- Name -->
                            <div class="form-group row">
                                <label class="col-md-3 col-form-label">{{ $t('words.name')}}</label>

                                <div class="col-md-9">
                                    <input id="edit-client-name" type="text" class="form-control"
                                           @keyup.enter="update" v-model="editForm.name">

                                    <span class="form-text text-muted">
                                        {{ $t('paragraphs.oauth_name_tip')}}
                                    </span>
                                </div>
                            </div>

                            <!-- Redirect URL -->
                            <div class="form-group row">
                                <label class="col-md-3 col-form-label">{{ $t('words.redirect_url')}}</label>

                                <div class="col-md-9">
                                    <input type="text" class="form-control" name="redirect"
                                           @keyup.enter="update" v-model="editForm.redirect">

                                    <span class="form-text text-muted">
                                        {{ $t('paragraphs.oauth_redirect_url_tip')}}
                                    </span>
                                </div>
                            </div>
                        </form>
                    </div>

                    <!-- Modal Actions -->
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-dismiss="modal">{{ $t('words.close')}}</button>

                        <button type="button" class="btn btn-primary" @click="update">
                            {{ $t('words.save')}}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    /*
     * The component's data.
     */
    props: {
        haveSubmitRight: {
            type: [Boolean],
            require: true,
        },
    },
    data() {
        return {
            meta: {
                last_page: 0,
                current_page: 1,
                total: 0,
                per_page: 5,
            },
            clients: [],

            createForm: {
                errors: [],
                name: '',
                redirect: '',
            },

            editForm: {
                errors: [],
                name: '',
                redirect: '',
            },
        };
    },

    /**
     * Prepare the component (Vue 1.x).
     */
    ready() {
        this.prepareComponent();
    },

    /**
     * Prepare the component (Vue 2.x).
     */
    mounted() {
        this.prepareComponent();
    },

    methods: {
        moveCurrentPage(page) {
            this.meta.current_page = page;
        },
        /**
         * Prepare the component.
         */
        prepareComponent() {
            this.getClients();

            $('#modal-create-client').on('shown.bs.modal', () => {
                $('#create-client-name').focus();
            });

            $('#modal-edit-client').on('shown.bs.modal', () => {
                $('#edit-client-name').focus();
            });
        },

        /**
         * Get all of the OAuth clients for the user.
         */
        getClients() {
            const self = this;
            axios.get('/oauth/clients')
                .then((response) => {
                    self.clients = response.data;
                    self.meta.total = self.clients.length;
                    self.meta.last_page = parseInt(self.meta.total / self.meta.per_page, 10);
                    if (self.meta.total % self.meta.per_page !== 0) {
                        self.meta.last_page += 1;
                    }
                    self.meta.current_page = 1;
                });
        },

        /**
         * Show the form for creating new clients.
         */
        showCreateClientForm() {
            $('#modal-create-client').modal('show');
        },

        /**
         * Create a new OAuth client for the user.
         */
        store() {
            this.persistClient(
                'post', '/oauth/clients',
                this.createForm, '#modal-create-client',
            );
        },

        /**
         * Edit the given client.
         */
        edit(client) {
            this.editForm.id = client.id;
            this.editForm.name = client.name;
            this.editForm.redirect = client.redirect;

            $('#modal-edit-client').modal('show');
        },

        /**
         * Update the client being edited.
         */
        update() {
            this.persistClient(
                'put', `/oauth/clients/${this.editForm.id}`,
                this.editForm, '#modal-edit-client',
            );
        },

        /**
         * Persist the client to storage using the given form.
         */
        persistClient(method, uri, form, modal) {
            if (!this.haveSubmitRight) {
                this.$toasted.error(this.$t('paragraphs.error.check_login_right'));
                return;
            }

            form.errors = [];

            axios[method](uri, form)
                .then((response) => {
                    this.getClients();

                    form.name = '';
                    form.redirect = '';
                    form.errors = [];

                    $(modal).modal('hide');
                })
                .catch((error) => {
                    if (typeof error.response.data === 'object') {
                        form.errors = _.flatten(_.toArray(error.response.data.errors));
                    } else {
                        form.errors = [this.$t('paragraphs.something_wrong')];
                    }
                });
        },

        /**
         * Destroy the given client.
         */
        destroy(client) {
            if (!this.haveSubmitRight) {
                this.$toasted.error(this.$t('paragraphs.error.check_login_right'));
                return;
            }
            const confirmMessage = this.$t('paragraphs.message.confirm.remove_item');
            if (confirm(confirmMessage)) {
                axios.delete(`/oauth/clients/${client.id}`)
                    .then((response) => {
                        this.getClients();
                    });
            }
        },
    },
    computed: {
        currentClients() {
            const start = (this.meta.current_page-1) * this.meta.per_page;
            return _.slice(this.clients, start, start + this.meta.per_page);
        },
    },
};
</script>
